import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const DocTimeBarChart = ({ data, isLoading }) => {
  const bracketColors = {
    green: "rgba(0, 128, 0, 0.6)", // Green
    yellow: "rgba(255, 205, 86, 0.6)", // Yellow
    red: "rgba(255, 99, 132, 0.6)", // Red
  };

  const [bracketData, setBracketData] = useState({});

  useEffect(() => {
    const calculateBracketData = () => {
      const tempData = {};

      if (data && Array.isArray(data)) {
        data.forEach((item) => {
          const entryPoint =
            item.header?.pedimento?.pedimento_entry_point || "Unknown";
          const docTime = calculateDocTimeValue(
            item.header?.detail?.dates_data
          );

          if (!tempData[entryPoint]) {
            tempData[entryPoint] = { green: 0, yellow: 0, red: 0 };
          }

          if (docTime <= 20) tempData[entryPoint].green += 1;
          else if (docTime <= 40) tempData[entryPoint].yellow += 1;
          else tempData[entryPoint].red += 1;
        });
      }

      setBracketData(tempData);
    };

    if (!isLoading && data && data.length > 0) {
      calculateBracketData();
    }
  }, [data, isLoading]);

  const entryPoints = Object.keys(bracketData);

  const chartData = {
    labels: entryPoints,
    datasets: [
      {
        label: "Green (0-20 hours)",
        data: entryPoints.map(
          (entryPoint) => bracketData[entryPoint]?.green || 0
        ),
        backgroundColor: bracketColors.green,
      },
      {
        label: "Yellow (21-40 hours)",
        data: entryPoints.map(
          (entryPoint) => bracketData[entryPoint]?.yellow || 0
        ),
        backgroundColor: bracketColors.yellow,
      },
      {
        label: "Red (41+ hours)",
        data: entryPoints.map(
          (entryPoint) => bracketData[entryPoint]?.red || 0
        ),
        backgroundColor: bracketColors.red,
      },
    ],
  };

  const options = {
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
      },
      x: {
        stacked: true,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
      datalabels: {
        color: "#fff",
        display: true,
        formatter: (value) => {
          return value > 0 ? value : "";
        },
      },
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="text-blue-600 mr-2" />
          Doc. Time Distribution by Entry Point
        </h2>
      </div>
      <div className="flex-grow relative">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-xl"
            />
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

const calculateDocTimeValue = (datesData) => {
  const dateInit = new Date(datesData?.date_init);
  const dateDoda = new Date(datesData?.date_doda);
  if (
    isNaN(dateInit.getTime()) ||
    isNaN(dateDoda.getTime()) ||
    dateDoda <= dateInit
  ) {
    return Number.MAX_VALUE;
  }
  return (dateDoda - dateInit) / (1000 * 60 * 60);
};

export default DocTimeBarChart;
